import type { AIToolsConfigService } from '@affine/core/modules/ai-button';
import type { ServerService } from '@affine/core/modules/cloud';
import type { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import {
  FEATURE_KEY_ASSISTANT_TELEGRAM,
  isFeatureAllowedFromBody,
} from '@affine/core/modules/streamlain/hooks';
import {
  STREAMLAIN_COMPACT_HEADER_AREA_LIT_CSS,
  STREAMLAIN_HEADER_BORDER_WIDTH,
} from '@affine/core/modules/streamlain/shared/compact-header-layout';
import type { AppThemeService } from '@affine/core/modules/theme';
import type {
  CopilotChatHistoryFragment,
  UpdateChatSessionInput,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine/components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { type NotificationService } from '@blocksuite/affine/shared/services';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import type { EditorHost } from '@blocksuite/affine/std';
import { ShadowlessElement } from '@blocksuite/affine/std';
import type { ExtensionType, Store } from '@blocksuite/affine/store';
import { ArrowDownSmallIcon, CenterPeekIcon } from '@blocksuite/icons/lit';
import { css, html, nothing, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';

import type { SearchMenuConfig } from '../components/ai-chat-add-context';
import type { DocDisplayConfig } from '../components/ai-chat-chips';
import type {
  AINetworkSearchConfig,
  AIPlaygroundConfig,
  AIReasoningConfig,
} from '../components/ai-chat-input';
import type { ChatStatus } from '../components/ai-chat-messages';
import { createPlaygroundModal } from '../components/playground/modal';
import type { AppSidebarConfig } from './chat-config';

export interface BotOptionData {
  id: string | null;
  name: string;
  description: string;
}

export class AIChatPanelTitle extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .ai-chat-panel-title {
      ${unsafeCSS(STREAMLAIN_COMPACT_HEADER_AREA_LIT_CSS)}
      border-bottom: ${unsafeCSS(STREAMLAIN_HEADER_BORDER_WIDTH)} solid
        ${unsafeCSSVarV2('layer/insideBorder/border')};

      svg {
        width: 18px;
        height: 18px;
        color: var(--streamlain-text-secondary-color);
      }

      .chat-panel-title-text {
        display: flex;
        align-items: center;
        gap: 6px;
        flex: 1;
        min-width: 0;
        font-size: 14px;
        font-weight: 500;
        color: var(--streamlain-text-secondary-color);
      }

      .chat-panel-bot-switcher {
        display: flex;
        align-items: center;
        gap: 2px;
        max-width: 100%;
        min-width: 0;
        cursor: pointer;
        padding: 2px 4px;
        border-radius: 4px;
        transition: background-color 0.15s ease;
      }

      .chat-panel-bot-switcher:hover {
        background-color: var(--streamlain-hover-color);
      }

      .chat-panel-bot-label {
        padding: 2px 4px;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .chat-panel-bot-switcher-icon {
        flex: 0 0 auto;
        font-size: 16px;
        line-height: 0;
        color: var(--streamlain-text-secondary-color);
      }

      .chat-panel-playground {
        cursor: pointer;
        padding: 2px;
        margin-left: 8px;
        margin-right: auto;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .chat-panel-playground:hover svg {
        color: ${unsafeCSSVarV2('icon/activated')};
      }

      .chat-panel-telegram-bind {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        border: 1px solid var(--streamlain-primary-color);
        border-radius: 6px;
        background: var(--streamlain-background-secondary-color);
        color: var(--streamlain-primary-color);
        cursor: pointer;
        font-size: 12px;
        font-weight: 600;
        height: 28px;
        padding: 0 10px;
        white-space: nowrap;
      }

      .chat-panel-telegram-bind:hover {
        color: var(--streamlain-primary-color);
        background: color-mix(
          in srgb,
          var(--streamlain-primary-color) 12%,
          var(--streamlain-background-secondary-color)
        );
      }

      @media (max-width: 480px) {
        .chat-panel-telegram-bind {
          max-width: 86px;
          padding: 0 8px;
        }
      }
    }
  `;

  /**
   * sl-uq2j: workspaceId is primary scope. doc/host optional (not present on non-doc pages).
   */
  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor host: EditorHost | undefined;

  @property({ attribute: false })
  accessor doc: Store | undefined;

  @property({ attribute: false })
  accessor playgroundConfig!: AIPlaygroundConfig;

  @property({ attribute: false })
  accessor appSidebarConfig!: AppSidebarConfig;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor reasoningConfig!: AIReasoningConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor extensions!: ExtensionType[];

  @property({ attribute: false })
  accessor serverService!: ServerService;

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor affineWorkspaceDialogService!: WorkspaceDialogService;

  @property({ attribute: false })
  accessor affineThemeService!: AppThemeService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor status!: ChatStatus;

  @property({ attribute: false })
  accessor newSession!: () => void;

  @property({ attribute: false })
  accessor togglePin!: () => void;

  @property({ attribute: false })
  accessor openSession!: (sessionId: string) => void;

  @property({ attribute: false })
  accessor openDoc!: (docId: string, sessionId: string) => void;

  @property({ attribute: false })
  accessor deleteSession!: (session: BlockSuitePresets.AIRecentSession) => void;

  @property({ attribute: false })
  accessor updateSession!: (options: UpdateChatSessionInput) => Promise<void>;

  @property({ attribute: false })
  accessor bots: BotOptionData[] = [];

  @property({ attribute: false })
  accessor selectedBotId: string | null = null;

  @property({ attribute: false })
  accessor onSelectBot: ((botId: string | null) => void) | undefined;

  @property({ attribute: false })
  accessor onBindTelegramThread: (() => Promise<void>) | undefined = undefined;

  @property({ attribute: false })
  accessor isMemoryEnabled: boolean = false;

  @state()
  accessor telegramAllowed = isFeatureAllowedFromBody(
    FEATURE_KEY_ASSISTANT_TELEGRAM
  );

  private readonly updateTelegramAllowed = () => {
    const allowed = isFeatureAllowedFromBody(FEATURE_KEY_ASSISTANT_TELEGRAM);
    if (this.telegramAllowed !== allowed) {
      this.telegramAllowed = allowed;
    }
  };

  private get _selectedBotName(): string {
    if (!this.isMemoryEnabled) return 'STREAMLAIN AI';
    const bot = this.bots.find(b => b.id === this.selectedBotId);
    return (
      bot?.name ??
      this.bots[0]?.name ??
      I18n.t('com.streamlain.ai.bots.personal-assistant')
    );
  }

  private get hasSelectableBots(): boolean {
    return this.isMemoryEnabled && this.bots.length > 1;
  }

  override connectedCallback() {
    super.connectedCallback();

    this.updateTelegramAllowed();
    const featureClassObserver = new MutationObserver(
      this.updateTelegramAllowed
    );
    featureClassObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
    this.disposables.add(() => featureClassObserver.disconnect());
  }

  private readonly openBotSwitcher = (e: Event) => {
    if (!this.hasSelectableBots) return;
    const element = e.currentTarget;
    if (!(element instanceof HTMLElement)) return;

    const botItems = this.bots.map(bot =>
      menu.action({
        name: bot.name,
        isSelected: bot.id === this.selectedBotId,
        label: () => html`
          <span style="display:flex;flex-direction:column;gap:2px">
            <span
              style="font-size:13px;font-weight:${bot.id === this.selectedBotId
                ? '600'
                : '400'}"
              >${bot.name}</span
            >
            ${bot.description
              ? html`<span
                  style="font-size:11px;color:${unsafeCSSVarV2(
                    'text/secondary'
                  )}"
                  >${bot.description}</span
                >`
              : nothing}
          </span>
        `,
        select: () => {
          this.onSelectBot?.(bot.id);
        },
      })
    );

    const handler = popMenu(popupTargetFromElement(element), {
      options: { items: [menu.group({ items: botItems })] },
    });
    if (handler.menu?.menuElement) {
      handler.menu.menuElement.style.minWidth = '280px';
    }
  };

  private readonly openPlayground = () => {
    const playgroundContent = html`
      <playground-content
        .host=${this.host}
        .doc=${this.doc}
        .networkSearchConfig=${this.networkSearchConfig}
        .reasoningConfig=${this.reasoningConfig}
        .playgroundConfig=${this.playgroundConfig}
        .appSidebarConfig=${this.appSidebarConfig}
        .searchMenuConfig=${this.searchMenuConfig}
        .docDisplayConfig=${this.docDisplayConfig}
        .extensions=${this.extensions}
        .serverService=${this.serverService}
        .affineFeatureFlagService=${this.affineFeatureFlagService}
        .affineThemeService=${this.affineThemeService}
        .notificationService=${this.notificationService}
        .affineWorkspaceDialogService=${this.affineWorkspaceDialogService}
        .aiToolsConfigService=${this.aiToolsConfigService}
      ></playground-content>
    `;

    createPlaygroundModal(playgroundContent, 'AI Playground');
  };

  override render() {
    return html`
      <div class="ai-chat-panel-title">
        <div class="chat-panel-title-text">
          ${this.hasSelectableBots
            ? html`<span
                class="chat-panel-bot-switcher"
                data-testid="chat-panel-bot-switcher"
                @click=${this.openBotSwitcher}
                title=${this._selectedBotName}
              >
                ${this._selectedBotName}
                <span class="chat-panel-bot-switcher-icon"
                  >${ArrowDownSmallIcon()}</span
                >
              </span>`
            : html`<span class="chat-panel-bot-label"
                >${this._selectedBotName}</span
              >`}
        </div>
        ${this.playgroundConfig.visible.value
          ? html`
              <div class="chat-panel-playground" @click=${this.openPlayground}>
                ${CenterPeekIcon()}
              </div>
            `
          : nothing}
        ${this.telegramAllowed && this.onBindTelegramThread
          ? html`<button
              class="chat-panel-telegram-bind"
              title=${I18n.t('com.streamlain.ai.chat.telegram.bind-button')}
              @click=${() => this.onBindTelegramThread?.()}
            >
              ${I18n.t('com.streamlain.ai.chat.telegram.bind-button')}
            </button>`
          : nothing}
        <ai-chat-toolbar
          .session=${this.session}
          .workspaceId=${this.workspaceId}
          .docId=${this.doc?.id}
          .status=${this.status}
          .onNewSession=${this.newSession}
          .onTogglePin=${this.togglePin}
          .onOpenSession=${this.openSession}
          .onSessionDelete=${this.deleteSession}
          .onSessionRename=${this.updateSession}
          .notificationService=${this.notificationService}
        ></ai-chat-toolbar>
      </div>
    `;
  }
}
